import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { ExportAlmMethod } from '@models/export-alm/export-alm-method';
import { IExportAlmRequest } from '@models/export-alm/export-alm-request';
import { IRunOffsDetail } from '@models/export-alm/run-offs-detail';
import { applyProductSortingRules, IProduct, IProductGroup, IProductTab } from '@models/product';
import { VintageRunOffType } from '@models/product-detail-report/vintage-run-off';
import { ProductStatus } from '@models/record-status';
import { GroupResult } from '@progress/kendo-data-query';
import { ExportAlmService } from '@services/export/export-alm.service';
import { flat } from '@shared/arrays';
import { getDateString, getLastDayOfMonth } from '@shared/dates';
import { debounceFunct } from '@shared/debounce';
import { numberToString } from '@shared/numbers';
import { v4 as uuid } from 'uuid';

@Component({
  selector: 'app-export-alm-config',
  templateUrl: './export-alm-config.component.html',
  styleUrls: ['./export-alm-config.component.scss']
})
export class ExportALMConfigComponent implements OnInit {
  private runOffsDetails: IRunOffsDetail[] = null;

  public activeSelected = true;
  public asOfDate: Date = null;
  public errorLoading = false;
  public expectedRows: number = null;
  public exportMethod: ExportAlmMethod = ExportAlmMethod.NonMaturity;
  public forecastPeriods = [12, 18, 24, 36, 48, 60];
  public isAllProductsChecked = true;
  public isLoading = true;
  public isSelectAllDisabled = false;
  public maxAsOfDate: Date = null;
  public minAsOfDate: Date = null;
  public productGroups: IProductGroup[] = [];
  public products: IProduct[] = [];
  public productTabForm = new FormGroup({ tabName: new FormControl() });
  public productTabs: IProductTab[] = [];
  public productTabsOptions: any[];
  public selectedForecastPeriod = 24;
  public selectedProductTab: IProductTab = null;
  public updatingExpectedRows = false;

  get isDatePickerDisabled() {
    return this.products.length === 0 || this.runOffsDetails.length === 0;
  }

  get checkedProducts() {
    return this.products.filter(p => p.isChecked);
  }

  get checkedProductIds() {
    return this.checkedProducts
      .map(p => p.ProductId)
      .filter(Number)
      .sort((a, b) => a - b);
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { productTabs: IProductTab[] },
    private readonly exportALMService: ExportAlmService,
    public dialogRef: MatDialogRef<ExportALMConfigComponent>
  ) {}

  async ngOnInit() {
    await this.loadData();
    this.updateDisabledProducts();
    this.updateSelectedProducts();
  }

  private async loadData() {
    try {
      this.isLoading = true;
      this.errorLoading = false;
      this.runOffsDetails = await this.exportALMService.getRunOffsDetails().toPromise();
      this.setMinAndMaxDates();
      this.productTabs = this.data.productTabs; // .sort((a: IProductTab, b: IProductTab) => (a.Name < b.Name ? -1 : 0));
      this.setupProductTabs();
    } catch (error) {
      console.warn(error);
      this.errorLoading = true;
    } finally {
      this.isLoading = false;
    }
  }

  private setupProductTabs() {
    this.selectedProductTab = this.productTabs.find(tab => tab.IsPublished);

    this.productTabsOptions = [
      {
        Name: 'All Products',
        Id: -1
      },
      ...(this.productTabs || []).map(t => ({ Name: t.Name, Id: t.Id }))
    ];

    const option = this.productTabsOptions.find(({ Id }) => Id === this.selectedProductTab.Id);
    this.productTabForm.controls.tabName.setValue(option, { emitEvent: false });

    this.selectProductTab(option);
  }

  public selectProductTab(opt) {
    this.selectedProductTab = opt && opt.Id > 0 ? this.productTabs.find(tab => tab.Id === opt.Id) : null;

    // Get Products from selected / all tabs
    this.products = applyProductSortingRules(
      this.selectedProductTab ? this.selectedProductTab.Products : flat(this.productTabs.map(t => t.Products))
    ).filter(p => p.RecordStatus === ProductStatus.Active);

    // Get ProductGroups for Products
    this.productGroups = [];
    this.products.forEach((p: IProduct) => {
      if (!this.productGroups.find(g => g.ProductGroupId === p.ProductGroupId)) {
        this.productGroups.push({
          GroupName: p.ProductGroupName,
          ProductGroupId: p.ProductGroupId,
          IsActive: true,
          Description: null
        });
      }
    });
    this.productGroups = this.productGroups.sort((a, b) => (a.GroupName.toLowerCase() < b.GroupName.toLowerCase() ? -1 : 0));

    this.updateSelectedProducts();
  }

  public onCalibrationFilterChanged(changed: boolean) {
    if (changed !== this.activeSelected) {
      this.activeSelected = changed;
      this.updateDisabledProducts();
      this.updateSelectedProducts();
    }
  }

  public onAllProductsToggled() {
    setTimeout(() => {
      this.productGroups.forEach((group: any) => {
        const groupProducts = this.products.filter(p => p.ProductGroupId === group.ProductGroupId);
        const enabledProducts = groupProducts.filter((p: IProduct) => !p.isDisabled);
        group.isChecked = this.isAllProductsChecked && enabledProducts.length > 0;
        enabledProducts.forEach((p: IProduct) => (p.isChecked = this.isAllProductsChecked));
      });
      this.updateSelectedProducts();
    });
  }

  public onGroupToggled(group: GroupResult | any) {
    setTimeout(() => {
      const groupProducts = this.products.filter(p => p.ProductGroupId === group.ProductGroupId);
      groupProducts.filter((p: IProduct) => !p.isDisabled).forEach((p: IProduct) => (p.isChecked = group.isChecked));
      this.updateSelectedProducts();
    });
  }

  public onProductToggled(product: IProduct) {
    setTimeout(() => {
      const group = this.productGroups.find(g => g.ProductGroupId === product.ProductGroupId);
      if (group) {
        group.isChecked = this.products.filter(p => p.ProductGroupId === group.ProductGroupId).every(p => p.isChecked);
      }

      this.updateSelectedProducts();
    });
  }

  private updateDisabledProducts() {
    if (!this.minAsOfDate || !this.maxAsOfDate || !this.runOffsDetails) {
      return;
    }

    this.productGroups.forEach(group => {
      const groupProducts = this.products.filter(p => p.ProductGroupId === group.ProductGroupId);

      groupProducts.forEach((product: IProduct) => {
        const runOffDetail = this.activeSelected
          ? this.runOffsDetails.find(r => r.ProductId === product.ProductId && r.RunOffType === VintageRunOffType.ActiveActual)
          : this.runOffsDetails.find(
              r =>
                r.ProductId === product.ProductId &&
                [VintageRunOffType.ProposedActual, VintageRunOffType.ActiveActual].includes(r.RunOffType)
            );

        product.isDisabled =
          !runOffDetail ||
          this.asOfDate.getTime() < new Date(runOffDetail.DateFrom).getTime() ||
          this.asOfDate.getTime() > new Date(runOffDetail.LastObservationDate).getTime();

        if (product.isDisabled) {
          product.isChecked = false;
        }
      });
      group.isDisabled = groupProducts.every((p: IProduct) => p.isDisabled);
      const enabledProducts = groupProducts.filter((p: IProduct) => !p.isDisabled);
      group.isChecked = !group.isDisabled && enabledProducts.length > 0 && enabledProducts.every((p: IProduct) => p.isChecked);
    });
  }

  private updateSelectedProducts() {
    debounceFunct(
      () => {
        this.productGroups.forEach((group: any) => {
          const groupProducts = this.products.filter(p => p.ProductGroupId === group.ProductGroupId);
          group.isDisabled = groupProducts.length === 0 || groupProducts.every((p: IProduct) => p.isDisabled);
          const enabledProducts = groupProducts.filter((p: IProduct) => !p.isDisabled);
          group.isChecked = !group.Disabled && enabledProducts.length > 0 && enabledProducts.every((p: IProduct) => p.isChecked);
        });

        const enabledGroups = this.productGroups.filter((pg: any) => !pg.isDisabled);
        this.isAllProductsChecked = enabledGroups.length > 0 && enabledGroups.every(g => g.isChecked);
        this.isSelectAllDisabled = this.productGroups.every(g => g.isDisabled);
        this.updateExpectedRows();
      },
      50,
      'export-alm-config'
    );
  }

  public onDateChanged() {
    this.asOfDate = getLastDayOfMonth(this.asOfDate);
    this.updateDisabledProducts();
    this.updateSelectedProducts();
    this.updateExpectedRows();
  }

  private async updateExpectedRows() {
    if (this.asOfDate && this.exportMethod === ExportAlmMethod.Vintage && this.checkedProducts.length > 0) {
      this.updatingExpectedRows = true;
      const request: IExportAlmRequest = {
        ProductIds: this.checkedProductIds,
        // @ts-ignore
        AsOfDate: getDateString(this.asOfDate),
        ExportMethod: this.exportMethod,
        OnlyActiveCalibrations: this.activeSelected
      };

      this.exportALMService
        .getExpectedRows(request)
        .toPromise()
        .then((response: { rows: number }) => {
          this.expectedRows = response ? response.rows : 0;
          this.updatingExpectedRows = false;
        });
    }
  }

  public getExpectedRowS() {
    return this.expectedRows ? `${this.expectedRows < 1000 ? '' : '~ '}${numberToString(this.expectedRows, 1)} expected CP records` : '';
  }

  private setMinAndMaxDates() {
    if (this.runOffsDetails && this.runOffsDetails.length > 0) {
      try {
        const dates = Array.from(new Set(flat(this.runOffsDetails.map(r => [r.DateFrom, r.LastObservationDate])))).sort(
          (a, b) => new Date(a).getTime() - new Date(b).getTime()
        );
        this.minAsOfDate = new Date(dates[0]);
        this.maxAsOfDate = new Date(dates[dates.length - 1]);
      } catch (error) {
        console.warn({ error });
      }

      if (!this.asOfDate || this.asOfDate > this.maxAsOfDate) {
        this.asOfDate = this.maxAsOfDate;
      } else if (this.asOfDate < this.minAsOfDate) {
        this.asOfDate = this.minAsOfDate;
      }
    }
  }

  public onExportMethodChanged(event) {
    // exportMethod is bound to the input
    this.updateExpectedRows();
  }

  /**
   * @param BuildReportClicked If True, export will start
   */
  public closeDialog(BuildReportClicked: boolean = false) {
    this.isLoading = true;
    const request: IExportAlmRequest = {
      Id: uuid(),
      BuildReportClicked,
      ProductIds: this.checkedProductIds,
      AsOfDate: this.asOfDate,
      ExportMethod: this.exportMethod,
      OnlyActiveCalibrations: this.activeSelected,
      ForecastPeriods: this.selectedForecastPeriod
    };

    this.dialogRef.close(request);
  }
}
